const API_DATE_PARTS_REGEX = /^(\d{4})-(\d{2})-(\d{2})/;

export const normalizeApiDate = (date: string): string => {
  const match = date.match(API_DATE_PARTS_REGEX);

  if (!match) {
    return date;
  }

  return `${match[1]}-${match[2]}-${match[3]}`;
};

const parseApiDateParts = (apiDate: string) => {
  const normalizedDate = normalizeApiDate(apiDate);
  const match = normalizedDate.match(API_DATE_PARTS_REGEX);

  if (!match) {
    const fallbackDate = new Date();

    return {
      year: fallbackDate.getFullYear(),
      month: fallbackDate.getMonth() + 1,
      day: fallbackDate.getDate(),
    };
  }

  return {
    year: Number(match[1]),
    month: Number(match[2]),
    day: Number(match[3]),
  };
};

const createLocalDateFromApiDate = (apiDate: string): Date => {
  const { year, month, day } = parseApiDateParts(apiDate);

  return new Date(year, month - 1, day);
};

export const formatDateForApi = (date: Date): string => {
  return date.toISOString().split("T")[0];
};

export const addDays = (date: Date, days: number): Date => {
  const copiedDate = new Date(date);
  copiedDate.setDate(copiedDate.getDate() + days);
  return copiedDate;
};

export const getDateRange = (startDate: string, endDate: string): string[] => {
  const dates: string[] = [];
  const currentDate = new Date(startDate);
  const lastDate = new Date(endDate);

  while (currentDate <= lastDate) {
    dates.push(formatDateForApi(currentDate));
    currentDate.setDate(currentDate.getDate() + 1);
  }

  return dates;
};

export const formatShortDisplayDate = (date: string): string => {
  const parsedDate = new Date(date);

  return parsedDate
    .toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "2-digit",
    })
    .replace(/\//g, ".");
};

export const formatLongDisplayDate = (date: string): string => {
  const parsedDate = createLocalDateFromApiDate(date);

  return parsedDate
    .toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    })
    .replace(/\//g, ".");
};

export const isDateBetweenBooking = (
  date: string,
  checkIn: string,
  checkOut: string,
): boolean => {
  const currentDate = new Date(date);
  const bookingStart = new Date(checkIn);
  const bookingEnd = new Date(checkOut);

  return currentDate >= bookingStart && currentDate < bookingEnd;
};

export const parseDisplayDate = (value: string): Date => {
  const [day, month, year] = value.split("-").map(Number);

  if (!day || !month || !year) {
    return new Date();
  }

  return new Date(year, month - 1, day);
};

export const formatDateToDisplay = (date: Date): string => {
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();

  return `${day}-${month}-${year}`;
};

export const displayDateToApiDate = (value: string): string => {
  const [day, month, year] = value.split("-");

  return `${year}-${month}-${day}`;
};

export const apiDateToDisplayDate = (value: string): string => {
  const [year, month, day] = value.split("-");

  return `${day}-${month}-${year}`;
};

export const addDaysToApiDate = (apiDate: string, days: number): string => {
  const date = new Date(apiDate);
  date.setDate(date.getDate() + days);

  return formatDateForApi(date);
};

export const isSameApiDate = (
  firstDate: string,
  secondDate: string,
): boolean => {
  return firstDate === secondDate;
};

export const isApiDateBefore = (
  firstDate: string,
  secondDate: string,
): boolean => {
  return new Date(firstDate) < new Date(secondDate);
};

export const isApiDateAfter = (
  firstDate: string,
  secondDate: string,
): boolean => {
  return new Date(firstDate) > new Date(secondDate);
};

export const isApiDateBetween = (
  date: string,
  startDate: string,
  endDate: string,
): boolean => {
  const current = new Date(date);
  const start = new Date(startDate);
  const end = new Date(endDate);

  return current > start && current < end;
};

export const bookingOverlapsRange = (
  bookingCheckIn: string,
  bookingCheckOut: string,
  rangeStart: string,
  rangeEnd: string,
): boolean => {
  return bookingCheckIn < rangeEnd && bookingCheckOut > rangeStart;
};
